import { useState } from "react";
import {
  Aviso,
  Botao,
  Cabecalho,
  Celula,
  CelulaCabecalho,
  Chip,
  Corpo,
  Linha,
  Segmentado,
  Tabela,
} from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  MODELO_DE_ORGANIZACOES,
  MODELO_DE_PESSOAS,
  ROTULO_DA_ACAO_DA_IMPORTACAO,
  montarCsvDeErros,
  useImportarPlanilha,
  type RelatorioDaImportacao,
  type TipoDeImportacao,
} from "../../nucleo/cadastro";

const TIPOS = [
  { valor: "organizacoes", rotulo: "Organizações" },
  { valor: "pessoas", rotulo: "Pessoas" },
] as const;

/** O CSV em um endereço `data:` para baixar, com BOM para o Excel reconhecer o UTF-8. */
function enderecoDoCsv(conteudo: string): string {
  const bom = String.fromCharCode(0xfeff);
  return `data:text/csv;charset=utf-8,${encodeURIComponent(bom + conteudo)}`;
}

const ESTADO_DA_ACAO = {
  criar: "ok",
  atualizar: "atencao",
  sem_mudanca: "neutro",
  erro: "erro",
} as const;

/**
 * Importação CSV em dois passos que usam **o mesmo arquivo**: a prévia não grava nada e mostra,
 * linha a linha, o que a planilha vai fazer; a aplicação grava o que está válido e devolve o
 * relatório (com os erros para baixar).
 */
export function PainelDeImportacao() {
  const [tipo, setTipo] = useState<TipoDeImportacao>("organizacoes");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [atualizar, setAtualizar] = useState(false);
  const [relatorio, setRelatorio] = useState<RelatorioDaImportacao | null>(null);
  const importar = useImportarPlanilha(tipo);

  function trocarTipo(novo: TipoDeImportacao) {
    setTipo(novo);
    setRelatorio(null);
    importar.reset();
  }

  function rodar(aplicar: boolean) {
    if (!arquivo) return;
    importar.mutate(
      { arquivo, aplicar, atualizarExistentes: atualizar },
      { onSuccess: setRelatorio },
    );
  }

  const modelo = tipo === "organizacoes" ? MODELO_DE_ORGANIZACOES : MODELO_DE_PESSOAS;
  const podeAplicar =
    relatorio !== null && !relatorio.aplicada && relatorio.criar + relatorio.atualizar > 0;

  return (
    <div data-testid="painel-de-importacao">
      <p className="lead mb-3">
        Suba uma planilha CSV (separada por vírgula ou ponto e vírgula). Primeiro veja a prévia —
        ela não grava nada —, depois aplique.
      </p>
      <Segmentado
        rotulo="O que importar"
        itens={TIPOS}
        valor={tipo}
        aoMudar={trocarTipo}
        className="mb-3"
      />
      <p className="nota-rodape mb-3">
        {tipo === "organizacoes"
          ? "Colunas: cnpj, razao_social, nome_fantasia, cidade, uf, segmento, classificacao. A chave é o CNPJ."
          : "Colunas: email, nome, cargo, telefone, cnpj (da organização), papel, superior_email. A chave é o e-mail. As pessoas entram sem acesso: convide depois, pela tela de usuários."}{" "}
        <a
          href={enderecoDoCsv(modelo)}
          download={`modelo-${tipo}.csv`}
          data-testid="baixar-modelo"
        >
          Baixar planilha modelo
        </a>
      </p>
      <div className="campo">
        <label className="campo__rotulo" htmlFor="arquivo-da-importacao">
          Planilha CSV
        </label>
        <input
          id="arquivo-da-importacao"
          type="file"
          accept=".csv,text/csv"
          data-testid="arquivo-da-importacao"
          onChange={(evento) => {
            setArquivo(evento.target.files?.[0] ?? null);
            setRelatorio(null);
            importar.reset();
          }}
        />
      </div>
      <div className="campo">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={atualizar}
            onChange={(evento) => {
              setAtualizar(evento.target.checked);
              setRelatorio(null);
            }}
          />
          <span>Atualizar também os que já existem (célula vazia não apaga o que está cadastrado)</span>
        </label>
      </div>
      {importar.isError ? (
        <Aviso tom="erro" className="mb-3">
          {mensagemDoConsole(importar.error)}
        </Aviso>
      ) : null}
      <div className="flex gap-2 mb-4">
        <Botao
          onClick={() => rodar(false)}
          disabled={!arquivo || importar.isPending}
          data-testid="previa-da-importacao"
        >
          {importar.isPending && !relatorio?.aplicada ? "Analisando…" : "Pré-visualizar"}
        </Botao>
        <Botao
          variante="verde"
          onClick={() => rodar(true)}
          disabled={!podeAplicar || importar.isPending}
          data-testid="aplicar-importacao"
        >
          Aplicar importação
        </Botao>
      </div>
      {relatorio ? <RelatorioDaImportacaoNaTela relatorio={relatorio} tipo={tipo} /> : null}
    </div>
  );
}

function RelatorioDaImportacaoNaTela({
  relatorio,
  tipo,
}: {
  relatorio: RelatorioDaImportacao;
  tipo: TipoDeImportacao;
}) {
  return (
    <section aria-labelledby="titulo-do-relatorio" data-testid="relatorio-da-importacao">
      <h2 className="titulo-secao" id="titulo-do-relatorio">
        {relatorio.aplicada ? "Resultado da importação" : "Prévia da importação"}
      </h2>
      {relatorio.aplicada ? (
        <Aviso tom={relatorio.com_erro > 0 ? "atencao" : "sucesso"} className="mb-3">
          {relatorio.aplicadas} linha(s) gravada(s)
          {relatorio.com_erro > 0 ? `; ${relatorio.com_erro} com erro, que não entraram` : ""}.
        </Aviso>
      ) : null}
      <ul className="mb-3" data-testid="resumo-da-importacao">
        <li>Linhas lidas: <b>{relatorio.total}</b></li>
        <li>A criar: <b>{relatorio.criar}</b></li>
        <li>A atualizar: <b>{relatorio.atualizar}</b></li>
        <li>Sem mudança: <b>{relatorio.sem_mudanca}</b></li>
        <li>Com erro: <b>{relatorio.com_erro}</b></li>
      </ul>
      {relatorio.colunas_ignoradas.length > 0 ? (
        <Aviso tom="neutro" className="mb-3">
          Colunas que não foram reconhecidas e serão ignoradas:{" "}
          {relatorio.colunas_ignoradas.join(", ")}.
        </Aviso>
      ) : null}
      {relatorio.com_erro > 0 ? (
        <p className="mb-3">
          <a
            href={enderecoDoCsv(montarCsvDeErros(relatorio))}
            download={`erros-da-importacao-de-${tipo}.csv`}
            data-testid="baixar-erros"
          >
            Baixar os erros (CSV)
          </a>
        </p>
      ) : null}
      <Tabela legenda="Linha a linha, o que a planilha faz e os erros encontrados.">
        <Cabecalho>
          <Linha>
            <CelulaCabecalho>Linha</CelulaCabecalho>
            <CelulaCabecalho>{tipo === "organizacoes" ? "CNPJ" : "E-mail"}</CelulaCabecalho>
            <CelulaCabecalho>Nome</CelulaCabecalho>
            <CelulaCabecalho>O que acontece</CelulaCabecalho>
          </Linha>
        </Cabecalho>
        <Corpo>
          {relatorio.linhas.map((linha) => (
            <Linha key={linha.linha} data-testid={`linha-da-importacao-${linha.linha}`}>
              <Celula>{linha.linha}</Celula>
              <Celula>{linha.chave ?? "—"}</Celula>
              <Celula>{linha.rotulo ?? "—"}</Celula>
              <Celula>
                <Chip estado={ESTADO_DA_ACAO[linha.acao]}>
                  {ROTULO_DA_ACAO_DA_IMPORTACAO[linha.acao]}
                </Chip>
                {linha.erros.map((erro) => (
                  <div key={erro} className="celula-secundaria">
                    {erro}
                  </div>
                ))}
              </Celula>
            </Linha>
          ))}
        </Corpo>
      </Tabela>
    </section>
  );
}
